import type { DependencyGraph } from "../dependency/types.js";
import type { TraceabilityReport } from "./types.js";
import { extractTraceElements } from "./elements.js";
import { resolveTraceLinks } from "./links.js";

export interface ValidateTraceabilityOptions {
  /** Resolved artifact definitions (from artifact-contracts.yaml). */
  artifacts: Record<
    string,
    {
      path_patterns: string[];
      exclude_patterns?: string[];
    }
  >;
  /** Trace link definitions from artifact-contracts.yaml. */
  traceLinks: Array<{
    id: string;
    from: string;
    to: string;
    resolver: string;
    description?: string;
  }>;
  /** System identifier from artifact-contracts.yaml. */
  system: string;
  /** Known file paths in the project (project-relative). */
  files: string[];
  /** Dependency graph for AST-based resolution. Optional. */
  graph?: DependencyGraph;
}

/**
 * Orchestrate trace element extraction, link resolution, and report assembly.
 */
export function validateTraceability(
  options: ValidateTraceabilityOptions,
): TraceabilityReport {
  const { artifacts, traceLinks, system, files, graph } = options;

  const artifactIds = new Set<string>();
  for (const link of traceLinks) {
    artifactIds.add(link.from);
    artifactIds.add(link.to);
  }

  const elementsByArtifact = new Map<string, ReturnType<typeof extractTraceElements>>();
  for (const id of artifactIds) {
    const def = artifacts[id];
    if (!def) {
      elementsByArtifact.set(id, []);
      continue;
    }
    elementsByArtifact.set(
      id,
      extractTraceElements({
        artifactId: id,
        pathPatterns: def.path_patterns,
        excludePatterns: def.exclude_patterns,
        files,
      }),
    );
  }

  const links = resolveTraceLinks({ traceLinks, elementsByArtifact, graph });

  let fullyConnected = 0;
  let partiallyConnected = 0;
  let disconnected = 0;

  for (const link of links) {
    const hasConnections = link.connections.length > 0;
    const noFromOrphans = link.orphans.from.length === 0;
    const noToOrphans = link.orphans.to.length === 0;

    if (hasConnections && noFromOrphans && noToOrphans) {
      fullyConnected++;
    } else if (hasConnections) {
      partiallyConnected++;
    } else {
      disconnected++;
    }
  }

  const orphanMap = new Map<string, (typeof links)[0]["orphans"]["from"][number]>();
  for (const link of links) {
    for (const el of [...link.orphans.from, ...link.orphans.to]) {
      orphanMap.set(`${el.artifactId}:${el.path}`, el);
    }
  }

  const orphanElements = [...orphanMap.values()].sort((a, b) =>
    a.path.localeCompare(b.path),
  );

  return {
    system,
    links,
    summary: {
      totalLinks: links.length,
      fullyConnected,
      partiallyConnected,
      disconnected,
    },
    orphanElements,
    generatedAt: new Date().toISOString(),
  };
}
